// PriorityPool.js
'use strict';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { priorityPool as priorityPoolDebug } from '../resource/debug.js';

const log = priorityPoolDebug.extend('log');
const errorLog = priorityPoolDebug.extend('error');

const defaultReleaseTimeoutMs = 2000;
const defaultPriorityScheduleDelayMs = 16;
const defaultMaxSize = 1;

// Lifecycle status of one pool entry.
const PoolResourceStatus = {
  PENDING: 'pending', // No seat yet.
  ALLOCATING: 'allocating', // Seat held; onAllocate running.
  ALLOCATED: 'allocated', // Ready.
  RELEASING: 'releasing', // onRelease running; seat still held.
};

// PENDING → ALLOCATING → ALLOCATED → RELEASING. ALLOCATING may jump to RELEASING.
const VALID_TRANSITIONS = {
  [PoolResourceStatus.PENDING]: [PoolResourceStatus.ALLOCATING],
  [PoolResourceStatus.ALLOCATING]: [
    PoolResourceStatus.ALLOCATED,
    PoolResourceStatus.RELEASING,
  ],
  [PoolResourceStatus.ALLOCATED]: [PoolResourceStatus.RELEASING],
  [PoolResourceStatus.RELEASING]: [],
};

/** Priority-based object pool. acquire → [onAllocate] → allocated → [onRelease]. */
class PriorityPool {
  constructor({
    maxSize = defaultMaxSize,
    releaseTimeoutMs = defaultReleaseTimeoutMs,
    priorityScheduleDelayMs = defaultPriorityScheduleDelayMs,
    onAllocate = NULL_FUNCTION,
    onRelease = NULL_FUNCTION,
    debugNameSpace,
  } = {}) {
    /** @type {Map<string, PoolResource>} */
    this._poolResources = new Map();
    // Same-id acquires wait for in-flight releases here.
    /** @type {Map<string, Promise>} */
    this._pendingReleases = new Map();

    this._maxSize = maxSize;
    this._releaseTimeoutMs = releaseTimeoutMs;
    this._priorityScheduleDelayMs = priorityScheduleDelayMs;
    this._onAllocate = onAllocate;
    this._onRelease = onRelease;
    // Per-instance debug namespace.
    this._log = debugNameSpace ? log.extend(debugNameSpace) : log;
    this._errorLog = debugNameSpace
      ? errorLog.extend(debugNameSpace)
      : errorLog;

    this._nextSequenceNumber = 0;

    // Debounced reconcile state.
    this._reconcileInFlight = null;
    this._reconcileTimerId = null;
    this._reconcileQueued = false;
  }

  /** Resolves on allocation; rejects if released first. */
  async acquire(
    id,
    { priority: rawPriority, onPreempted = NULL_FUNCTION, ...rest } = {}
  ) {
    const priority = this._ensurePriorityIsNumber(rawPriority);
    const options = { ...rest, priority, onPreempted };

    // Step 1. Same id in pool -> reuse.
    const activePoolResource = this._findActiveResource(id);
    if (activePoolResource) {
      return this._reacquireExisting(activePoolResource, options);
    }

    // Step 2. Same id releasing -> wait, then recheck.
    await this.waitForResourceRelease(id);
    const activePoolResourceAfterWait = this._findActiveResource(id);
    if (activePoolResourceAfterWait) {
      return this._reacquireExisting(activePoolResourceAfterWait, options);
    }

    // Step 3. New id.
    const poolResource = {
      ...options,
      id,
      status: PoolResourceStatus.PENDING,
      sequenceNumber: this._nextSequenceNumber++,
    };
    this._poolResources.set(id, poolResource);

    // 3.1 Free seat -> allocate.
    if (this._hasAvailableCapacity()) {
      this._allocatePoolResource(poolResource);
      return { poolResource: await this._waitForAllocation(poolResource) };
    }

    // 3.2 Preempt a strictly lower-priority holder.
    const evictionTarget = this._findEvictionTarget(priority);
    if (evictionTarget) {
      this._log('preempt', { id, evicting: evictionTarget.id });
      // The released object is still available for onPreempted.
      await this._releaseWithPreferred(evictionTarget.id, poolResource);
      this._invokeOnPreempted(evictionTarget.onPreempted, evictionTarget.id);
      return { poolResource: await this._waitForAllocation(poolResource) };
    }

    // 3.3 Queue.
    return { poolResource: await this._waitForAllocation(poolResource) };
  }

  // Same id, not mid-teardown.
  _findActiveResource(id) {
    const existing = this._poolResources.get(id);
    return existing && existing.status !== PoolResourceStatus.RELEASING
      ? existing
      : null;
  }

  // Refresh an existing entry and reuse its handle.
  async _reacquireExisting(poolResource, { priority, onPreempted, ...rest }) {
    const isPriorityChanged = poolResource.priority !== priority;
    Object.assign(poolResource, rest, { priority, onPreempted });

    if (isPriorityChanged) {
      this._scheduleReconcile();
    }

    return {
      poolResource:
        poolResource.status !== PoolResourceStatus.PENDING
          ? poolResource
          : await this._waitForAllocation(poolResource),
    };
  }

  // Centralized status transition; logs on illegal jumps.
  _transition(poolResource, newStatus) {
    const validTargets = VALID_TRANSITIONS[poolResource.status];
    if (!validTargets || !validTargets.includes(newStatus)) {
      this._errorLog('illegal-transition', {
        id: poolResource.id,
        from: poolResource.status,
        to: newStatus,
      });
    }
    poolResource.status = newStatus;
  }

  // Verify core pool invariants after lifecycle operations.
  _validatePoolState() {
    const { allocated, pending, maxSize } = this.getPoolStatus();
    if (allocated > maxSize) {
      this._errorLog('invariant-violated', {
        rule: 'allocated <= maxSize',
        allocated,
        maxSize,
      });
    }
    if (allocated < maxSize && pending > 0 && !this._reconcileTimerId) {
      this._errorLog('invariant-violated', {
        rule: 'free seats should not coexist with pending entries',
        allocated,
        maxSize,
        pending,
      });
    }
  }

  // Higher priority first; ties broken by earlier arrival (FIFO).
  _compare(entryA, entryB) {
    return (
      entryB.priority - entryA.priority ||
      entryA.sequenceNumber - entryB.sequenceNumber
    );
  }

  // Candidates are seated and not leaving; only strictly lower priority is evictable.
  _findEvictionTarget(incomingPriority) {
    let evictionTarget = null;
    for (const poolResource of this._poolResources.values()) {
      if (poolResource.status === PoolResourceStatus.PENDING) continue;
      if (poolResource.status === PoolResourceStatus.RELEASING) continue;
      if (poolResource.priority >= incomingPriority) continue;
      if (!evictionTarget || this._compare(evictionTarget, poolResource) < 0) {
        evictionTarget = poolResource;
      }
    }
    return evictionTarget;
  }

  _hasAvailableCapacity() {
    return this.getPoolStatus().allocated < this._maxSize;
  }

  // Waits until the resource is Allocated.
  _waitForAllocation(poolResource) {
    if (poolResource.status === PoolResourceStatus.ALLOCATED) {
      return Promise.resolve(poolResource);
    }
    if (poolResource._pendingAllocation) {
      return poolResource._pendingAllocation.promise;
    }

    const { promise, resolve, reject } = Promise.withResolvers();
    // Prevent unhandled rejection for fire-and-forget acquires.
    promise.catch(() => {});
    poolResource._pendingAllocation = { promise, resolve, reject };
    return promise;
  }

  // Resolve or reject the allocation waiter once.
  _settleAllocation(poolResource, { value, error }) {
    const pendingAllocation = poolResource._pendingAllocation;
    if (!pendingAllocation) return;
    poolResource._pendingAllocation = null;
    if (error) pendingAllocation.reject(error);
    else pendingAllocation.resolve(value);
  }

  updatePriority(id, priority) {
    const poolResource = this._poolResources.get(id);
    if (!poolResource) return;

    const priorityNumber = this._ensurePriorityIsNumber(priority);
    if (poolResource.priority === priorityNumber) return;

    poolResource.priority = priorityNumber;
    this._scheduleReconcile();
  }

  // Coalesce reconcile requests.
  _scheduleReconcile() {
    this._reconcileQueued = true;
    if (this._reconcileInFlight || this._reconcileTimerId) return;

    this._reconcileTimerId = setTimeout(() => {
      this._reconcileTimerId = null;
      this._runScheduledReconcile();
    }, this._priorityScheduleDelayMs);
  }

  // Drain queued reconcile passes.
  _runScheduledReconcile() {
    if (this._reconcileInFlight) return;

    this._reconcileInFlight = (async () => {
      try {
        while (this._reconcileQueued) {
          this._reconcileQueued = false;
          try {
            await this._reconcile();
          } catch (error) {
            this._errorLog('reconcile:error', { error });
          }
        }
      } finally {
        this._reconcileInFlight = null;
      }
    })();
  }

  // Full priority rebalance; may evict.
  async _reconcile() {
    const allResources = [...this._poolResources.values()];

    // Releasing entries hold seats but do not compete.
    const contenders = allResources.filter(
      poolResource => poolResource.status !== PoolResourceStatus.RELEASING
    );
    const releasingCount = allResources.length - contenders.length;
    const allocatableCount = Math.max(0, this._maxSize - releasingCount);

    const ranked = contenders.sort((poolResourceA, poolResourceB) =>
      this._compare(poolResourceA, poolResourceB)
    );
    const winners = new Set(ranked.slice(0, allocatableCount));

    const toEvict = contenders.filter(
      poolResource =>
        poolResource.status !== PoolResourceStatus.PENDING &&
        !winners.has(poolResource)
    );
    const toAllocate = contenders.filter(
      poolResource =>
        poolResource.status === PoolResourceStatus.PENDING &&
        winners.has(poolResource)
    );

    if (toEvict.length === 0 && toAllocate.length === 0) return;

    this._log('reconcile', {
      evictCount: toEvict.length,
      allocateCount: toAllocate.length,
    });

    // Evict before allocate; _allocatePoolResource does not re-check capacity.
    await Promise.all(
      toEvict.map(async poolResource => {
        const { id } = poolResource;
        if (this._poolResources.get(id) !== poolResource) return;
        this._transition(poolResource, PoolResourceStatus.RELEASING);
        try {
          await this._deallocatePoolResource(poolResource, 'reconcile');
        } finally {
          if (this._poolResources.get(id) === poolResource) {
            this._poolResources.delete(id);
          }
        }
        this._invokeOnPreempted(poolResource.onPreempted, id);
      })
    );
    // Some may already be promoted while evictions await.
    await Promise.all(
      toAllocate.map(poolResource => this._allocatePoolResource(poolResource))
    );
    this._validatePoolState();
  }

  // Fill one free seat with the highest-priority pending entry.
  // When preferred is given (eviction path), promote it first if still pending.
  async _promoteHighestPending(preferred) {
    if (!this._hasAvailableCapacity()) return;

    if (
      preferred &&
      this._poolResources.get(preferred.id) === preferred &&
      preferred.status === PoolResourceStatus.PENDING
    ) {
      await this._allocatePoolResource(preferred);
      return;
    }

    let highestPending = null;
    for (const poolResource of this._poolResources.values()) {
      if (poolResource.status !== PoolResourceStatus.PENDING) continue;
      if (!highestPending || this._compare(poolResource, highestPending) < 0) {
        highestPending = poolResource;
      }
    }
    if (!highestPending) return;

    await this._allocatePoolResource(highestPending);
  }

  // Reserve the seat before onAllocate to prevent over-allocation.
  // Idempotent; never throws.
  async _allocatePoolResource(poolResource) {
    const { id } = poolResource;
    if (this._poolResources.get(id) !== poolResource) return;
    if (poolResource.status !== PoolResourceStatus.PENDING) return;

    this._transition(poolResource, PoolResourceStatus.ALLOCATING);

    const onAllocateRun = (async () => {
      try {
        await this._onAllocate(id, poolResource);
        // Preserve RELEASING if release/preempt won the race.
        if (poolResource.status === PoolResourceStatus.ALLOCATING) {
          this._transition(poolResource, PoolResourceStatus.ALLOCATED);
        }
        if (poolResource.status === PoolResourceStatus.ALLOCATED) {
          this._settleAllocation(poolResource, { value: poolResource });
        } else {
          this._settleAllocation(poolResource, {
            error: this._buildCancelledAcquireError(
              id,
              'preempted-while-allocating'
            ),
          });
        }
        this._log('allocate', { id, debugNo: poolResource.debugNo });
      } catch (error) {
        this._errorLog('allocate:error', {
          id,
          error,
          debugNo: poolResource.debugNo,
        });
        this._settleAllocation(poolResource, { error });
        // If release/preempt took over, let that path clean up.
        if (
          this._poolResources.get(id) === poolResource &&
          poolResource.status === PoolResourceStatus.ALLOCATING
        ) {
          this._poolResources.delete(id);
          await this._promoteHighestPending();
        }
      } finally {
        if (this._poolResources.get(id) === poolResource) {
          poolResource._onAllocateRun = null;
        }
      }
    })();

    poolResource._onAllocateRun = onAllocateRun;
    await onAllocateRun;
    this._validatePoolState();
  }

  async _deallocatePoolResource(poolResource, type = 'release') {
    const { id } = poolResource;

    const releasePromise = (async () => {
      // Release after any in-flight allocation settles.
      if (poolResource._onAllocateRun) {
        await poolResource._onAllocateRun;
      }

      // Timeout is a watchdog only — logs but never short-circuits the
      // release. The seat stays held until onRelease actually completes,
      // preventing promote-before-resource-is-freed races.
      const timeoutId = setTimeout(() => {
        this._errorLog(`${type}:timeout`, {
          id,
          debugNo: poolResource.debugNo,
        });
      }, this._releaseTimeoutMs);
      try {
        await this._onRelease(id, poolResource);
      } catch (error) {
        this._errorLog(`${type}:error`, {
          id,
          error,
          debugNo: poolResource.debugNo,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    // Same-id acquires wait on this release.
    const tracked = releasePromise.finally(() => {
      if (this._pendingReleases.get(id) === tracked) {
        this._pendingReleases.delete(id);
      }
    });
    this._pendingReleases.set(id, tracked);
    await releasePromise;
  }

  async release(id) {
    return this._releaseWithPreferred(id, null);
  }

  /**
   * Release a pool entry and promote preferred into the freed slot.
   * @param {string} id - Entry to release.
   * @param {PoolResource|null} preferred - Pending entry to promote first; null falls back to highest-priority pending.
   */
  async _releaseWithPreferred(id, preferred) {
    const poolResource = this._poolResources.get(id);
    if (!poolResource) return;
    if (poolResource.status === PoolResourceStatus.RELEASING) return;

    if (poolResource.status === PoolResourceStatus.PENDING) {
      this._poolResources.delete(id);
      this._settleAllocation(poolResource, {
        error: this._buildCancelledAcquireError(id, 'release'),
      });
      this._log('release', {
        id,
        wasAllocated: false,
        debugNo: poolResource.debugNo,
      });
      return;
    }

    this._transition(poolResource, PoolResourceStatus.RELEASING);
    try {
      await this._deallocatePoolResource(poolResource);
    } finally {
      if (this._poolResources.get(id) === poolResource) {
        this._poolResources.delete(id);
      }
      this._log('release', {
        id,
        wasAllocated: true,
        debugNo: poolResource.debugNo,
      });
    }
    await this._promoteHighestPending(preferred);
    this._validatePoolState();
  }

  // Clear first so pending entries cannot be promoted.
  async releaseAll() {
    const poolResources = [...this._poolResources.values()];
    this._poolResources.clear();

    for (const poolResource of poolResources) {
      if (poolResource.status !== PoolResourceStatus.PENDING) continue;
      this._settleAllocation(poolResource, {
        error: this._buildCancelledAcquireError(poolResource.id, 'releaseAll'),
      });
    }

    await Promise.all(
      poolResources
        .filter(
          poolResource => poolResource.status !== PoolResourceStatus.PENDING
        )
        .map(poolResource =>
          this._deallocatePoolResource(poolResource, 'releaseAll')
        )
    );
  }

  async waitForResourceRelease(id) {
    const pendingReleasePromise = this._pendingReleases.get(id);
    if (!pendingReleasePromise) return;

    // Wait for the actual release to finish. The upper-layer acquire
    // timeout (raceWithTimeout) is the real deadlock safety net.
    await pendingReleasePromise.then(NULL_FUNCTION, NULL_FUNCTION);
  }

  setMaxSize(size) {
    if (typeof size !== 'number' || size === this._maxSize) return;

    this._maxSize = size;
    this._log('setMaxSize', { size });
    this._scheduleReconcile();
  }

  get(id) {
    return this._poolResources.get(id);
  }

  // Public iterator for allocated resources — avoids external access to _poolResources.
  forEachAllocated(callback) {
    for (const poolResource of this._poolResources.values()) {
      if (poolResource.status === PoolResourceStatus.ALLOCATED) {
        callback(poolResource);
      }
    }
  }

  getPoolStatus() {
    let allocated = 0;
    let releasing = 0;
    for (const poolResource of this._poolResources.values()) {
      if (poolResource.status !== PoolResourceStatus.PENDING) allocated++;
      if (poolResource.status === PoolResourceStatus.RELEASING) releasing++;
    }
    const size = this._poolResources.size;
    return {
      maxSize: this._maxSize,
      size,
      allocated,
      pending: size - allocated,
      releasing,
    };
  }

  _ensurePriorityIsNumber(priority) {
    return Number.isFinite(priority) ? priority : 0;
  }

  _invokeOnPreempted(onPreempted, id) {
    try {
      onPreempted?.();
    } catch (error) {
      this._errorLog('onPreempted:error', { id, error });
    }
  }

  _buildCancelledAcquireError(id, trigger) {
    return new Error(
      `PriorityPool: acquire('${id}') cancelled — released before allocation (${trigger})`
    );
  }
}

export default PriorityPool;
export { PriorityPool };
